import { useEffect, useState } from 'react';
import { BellRing, MessageCircle, Volume2, X } from 'lucide-react';
import { toast } from 'sonner';
import { notificationService } from '../services/notificationService';
import { settingsService } from '../services/settingsService';

type ReminderRow = {
  driverUserId?: string | null;
  driverName: string;
  locationAge: string;
};

type Props = {
  rows: ReminderRow[];
  onClose: () => void;
  onSent: () => void;
};

const DEFAULT_MESSAGE = 'Merhaba, uygulamadan konum paylaşımınızı kontrol edip tekrar konum göndermenizi rica ederiz.';

export function LocationReminderModal({ rows, onClose, onSent }: Props) {
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [sms, setSms] = useState(true);
  const [push, setPush] = useState(true);
  const [sound, setSound] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void settingsService.getSettingValue<any>('settings_system_preferences', {}).then((settings) => {
      if (!cancelled && String(settings?.locationReminderMessage || '').trim()) {
        setMessage(String(settings.locationReminderMessage));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const send = async () => {
    const userIds = [...new Set(rows.map((row) => Number(row.driverUserId)).filter((id) => Number.isInteger(id) && id > 0))];
    if (userIds.length === 0) return toast.error('Seçilen satırlarda sürücü hesabı bulunamadı');
    if (!message.trim()) return toast.error('Mesaj boş olamaz');
    if (!sms && !push) return toast.error('En az bir gönderim kanalı seçin');

    try {
      setSending(true);
      const response: any = await notificationService.sendLocationReminder({
        userIds,
        message: message.trim(),
        channels: { sms, push, sound: push && sound },
      });
      const result = response?.data || response;
      toast.success(`Hatırlatma gönderildi. SMS: ${result?.smsSent || 0}, Bildirim: ${result?.pushSent || 0}`);
      onSent();
      onClose();
    } catch (error: any) {
      toast.error(error?.message || 'Hatırlatma gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="w-full max-w-xl rounded-2xl bg-white p-5 shadow-2xl dark:bg-[#171717] dark:text-white">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">Konum Bildirimi Hatırlat</h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{rows.length} seçili kayıt</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-4 max-h-28 overflow-y-auto rounded-lg border border-slate-200 p-2 text-xs dark:border-slate-700">
          {rows.map((row, index) => <div key={`${row.driverUserId}-${index}`} className="flex justify-between gap-2 px-2 py-1"><span>{row.driverName}</span><span className="text-amber-600">{row.locationAge}</span></div>)}
        </div>

        <label className="mt-4 block text-sm font-semibold">Hatırlatma metni</label>
        <textarea value={message} onChange={(event) => setMessage(event.target.value)} rows={4} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-[#111] dark:text-white" />

        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700"><input type="checkbox" checked={sms} onChange={(event) => setSms(event.target.checked)} /><MessageCircle className="h-4 w-4 text-emerald-600" />SMS ile hatırlat</label>
          <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700"><input type="checkbox" checked={push} onChange={(event) => setPush(event.target.checked)} /><BellRing className="h-4 w-4 text-blue-600" />Bildirim gönder</label>
          <label className={`flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700 ${!push ? 'opacity-50' : ''}`}><input type="checkbox" checked={sound} disabled={!push} onChange={(event) => setSound(event.target.checked)} /><Volume2 className="h-4 w-4 text-orange-600" />Sesli bildir</label>
        </div>

        <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={onClose} disabled={sending} className="rounded-xl border border-slate-300 px-4 py-2 text-sm dark:border-slate-700">Vazgeç</button><button type="button" onClick={() => void send()} disabled={sending} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{sending ? 'Gönderiliyor...' : 'Gönder'}</button></div>
      </div>
    </div>
  );
}
